import Label from '@/components/Label/Label';
import { COLOR } from '@/utils/color';
import { Icon } from '@ant-design/compatible';
import { Button, Col, Divider, Modal, Row, Tooltip } from 'antd';
import React, { useState } from 'react';
import { showDeleteConfirm } from '../../showDeleteConfirm';
import ReconnectCloudAccount from '../../ReconnectCloudAccount/ReconnectCloudAccount';
import { forceCleanupConformation } from '../../forceCleanupConfirmation';
import { connect } from 'dva';
import { SCAN_STATUS } from '@/utils/constant';
import ReconnectGCPCloudAccount from '../../ReconnectCloudAccount/ReconnectGCPCloudAccount';
import ReconnectAzureCloudccount from '../../ReconnectCloudAccount/ReconnectAzureCloudccount';

const DeleteCloudAccountWrapper = props => {
  const {
    handleDeleteAccount,
    handleDisbaledAccount,
    showDisableAction,
    cloudAccountName,
    cloudAccountData,
    confirmCleanup,
    forceCleanupInProgress,
    allCloudAccountsList,
  } = props;
  const [reconnectModal, setReconnectModal] = useState(false);
  const isAWSAccount = cloudAccountData.provider.toLowerCase() === 'aws';
  const isGCPAccount = cloudAccountData.provider.toLowerCase() === 'gcp';
  const isAzureAccount = cloudAccountData.provider.toLowerCase() === 'azure';
  const isAdvancedAcc = cloudAccountData.protectionLevel.toLowerCase().includes('advance');

  const currentCloudAccount = allCloudAccountsList.find(each => cloudAccountData.id === each.id);
  const isForceCleanupExecuted =
    currentCloudAccount?.XRayScan?.statusMsg === 'Scan marked failed by force cleanup'
      ? true
      : false;

  const getColor = () => {
    if (isAdvancedAcc)
      if (!isXrayScanRunning || forceCleanupInProgress || isForceCleanupExecuted)
        return COLOR.DISABLED;
      else return COLOR.BTN.DELETE;
    return COLOR.BTN.DELETE; // this doesn't matter because we don't show force cleanup if its not a advance account
  };

  const getStatus = currentCloudAccount => {
    if (currentCloudAccount?.XRayScan?.status) {
      return [SCAN_STATUS.FAILED.status, SCAN_STATUS.RUNNING.status].some(each =>
        currentCloudAccount.XRayScan.status?.includes(each)
      );
    } else return false;
  };

  const isXrayScanRunning = !isAdvancedAcc
    ? false
    : getStatus(allCloudAccountsList.find(each => cloudAccountData.id === each.id));
  return (
    <>
      {
        <>
          <Row style={{ padding: 18 }} type="flex" justify="space-between">
            <Col span={12}>
              <Label title="Reconnect Cloud Account" />
              <p>
                Re-establish a fresh, error-free connection with Cloudnosys while retaining scan
                history. This resolves connectivity issues and ensures seamless integration.
              </p>
            </Col>
            <Col
              span={6}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                icon={<Icon type="disconnect" />}
                onClick={() => setReconnectModal(true)}
                type="primary"
                ghost
              >
                Reconnect
              </Button>
            </Col>
          </Row>
          <Divider />
          {isAdvancedAcc && (
            <>
              <Row style={{ padding: 18 }} type="flex" justify="space-between">
                <Col span={12}>
                  <Label title="Force Cleanup Operation" />
                  {isAWSAccount && (
                    <p>
                      The automatic cleanup function of the Cloudxray Scan feature did not execute
                      as expected due to an unforeseen issue. To prevent you from incurring
                      unnecessary costs, please manually delete the following AWS resources{' '}
                      <b>(AMIs, snapshots, volumes, instances, and Orchestrator)</b>.
                    </p>
                  )}
                  {isGCPAccount && (
                    <p>
                      This will trigger the force cleanup of all the temporary resources created by
                      Cloudnosys in your connected GCP project for CVEs and Malware scanning,
                      including VM instance/(s), Disk/(s), firewall rule/(s) etc.
                    </p>
                  )}

                  {isAzureAccount && (
                    <p>
                      The automatic cleanup function of the Cloudxray Scan feature did not execute
                      as expected due to an unforeseen issue. To prevent you from incurring
                      unnecessary costs, please manually delete the following Azure resources{' '}
                      <b>(disk, snapshots, and virtual machines)</b>.
                    </p>
                  )}
                </Col>
                <Col
                  span={4}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    title={
                      isForceCleanupExecuted
                        ? 'Force cleanup has already been executed'
                        : !isXrayScanRunning
                        ? 'Cloudxray Scan is not running'
                        : 'If scan fails, you can start cleanup by clicking here'
                    }
                  >
                    <Button
                      icon={<Icon type="clear" />}
                      style={{
                        borderColor: getColor(),
                        color: getColor(),
                        marginLeft: 10,
                      }}
                      onClick={() => forceCleanupConformation(confirmCleanup, cloudAccountName)}
                      loading={forceCleanupInProgress}
                      disabled={
                        forceCleanupInProgress || !isXrayScanRunning || isForceCleanupExecuted
                      }
                    >
                      Force Cleanup
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
              <Divider />
            </>
          )}
        </>
      }

      <Row style={{ padding: 18 }} type="flex" justify="space-between">
        <Col span={12}>
          <Label title="Delete Cloud Account" />
          <p>
            This action will make this cloud account permanently unusable. You will lose all its
            data and configurations. Deleting a cloud account is irreversible.
          </p>
        </Col>
        <Col
          span={4}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Button
            icon={<Icon type="delete" />}
            style={{ borderColor: COLOR.BTN.DELETE, color: COLOR.BTN.DELETE, marginLeft: 10 }}
            onClick={() =>
              showDeleteConfirm(handleDeleteAccount, handleDisbaledAccount, {
                showDisableAction,
                cloudAccountName,
              })
            }
          >
            Delete
          </Button>
        </Col>
      </Row>

      {
        <>
          <ReconnectModal
            open={reconnectModal}
            setOpen={setReconnectModal}
            cloudAccountData={cloudAccountData}
          />
        </>
      }
    </>
  );
};

export default connect(({ cloudAccount }) => ({
  forceCleanupInProgress: cloudAccount.forceCleanupInProgress,
  allCloudAccountsList: cloudAccount.allCloudAccountsList,
}))(DeleteCloudAccountWrapper);

const ReconnectModal = ({ open, setOpen, cloudAccountData }) => {
  return (
    <Modal
      width={1000}
      footer={false}
      open={open}
      onCancel={() => {
        setOpen(false);
      }}
      destroyOnClose
      bodyStyle={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Reconnect&nbsp;
          <span
            style={{
              color: '#1890ff',
              fontWeight: 'bold',
            }}
          >
            {cloudAccountData.accountName}
          </span>
          &nbsp;to Cloudnosys
        </div>
      }
    >
      {cloudAccountData.provider.toLowerCase() === 'gcp' ? (
        <ReconnectGCPCloudAccount
          onClose={() => setOpen(false)}
          cloudAccountData={cloudAccountData}
          updateProtecionLevelTo={cloudAccountData.protectionLevel}
          isForceFullyReconnecting={true}
        />
      ) : cloudAccountData.provider.toLowerCase() === 'aws' ? (
        <ReconnectCloudAccount
          onClose={() => setOpen(false)}
          cloudAccountData={cloudAccountData}
          updateProtecionLevelTo={cloudAccountData.protectionLevel}
          isForceFullyReconnecting={true}
        />
      ) : (
        <ReconnectAzureCloudccount
          onClose={() => setOpen(false)}
          cloudAccountData={cloudAccountData}
          updateProtecionLevelTo={cloudAccountData.protectionLevel}
          isForceFullyReconnecting={true}
        />
      )}
    </Modal>
  );
};
